import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Search, MessageCircle, HelpCircle, Package, Wrench, Clipboard, Sparkles, ShieldCheck, Rocket } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { SITE, breadcrumbForPage } from "@/lib/seo";

type FAQ = { q: string; a: string };
type Category = { id: string; label: string; icon: React.ElementType; items: FAQ[] };

const categories: Category[] = [
  {
    id: "planes",
    label: "Planes y suscripciones",
    icon: Package,
    items: [
      { q: "¿Cómo funcionan los planes?", a: "Eliges un plan según cuántos servicios necesitas: el plan de 1 servicio incluye 1 servicio a elegir, el de 2 servicios incluye 2 y el de 4 servicios incluye 4. Tú decides qué servicios quieres dentro de tu plan, y todos los servicios tienen el mismo alcance completo independientemente del plan." },
      { q: "¿Los precios incluyen IVA?", a: "Sí. Todos los precios que ves en la web incluyen IVA. Lo que ves es lo que pagas, sin sorpresas." },
      { q: "¿Hay permanencia?", a: "No. Todos los planes son cancelables mes a mes. Puedes cancelar cuando quieras y mantendrás el servicio activo hasta el final del ciclo que ya has pagado." },
      { q: "¿Cuándo empieza mi ciclo de facturación?", a: "El día que realizas el pago. Si contratas un día 14, tu ciclo va del 14 al 14 de cada mes, y ese día se renueva automáticamente." },
      { q: "¿Puedo cambiar los servicios elegidos de mi plan?", a: "Sí, puedes cambiar los servicios que tienes elegidos, y el cambio se aplica al inicio de tu siguiente ciclo de facturación. Durante el ciclo en curso se mantienen los servicios contratados." },
      { q: "¿Puedo subir o bajar de plan?", a: "Sí, en cualquier momento. El cambio se hace efectivo al inicio del siguiente ciclo, sin prorrateos ni cobros parciales a mitad de mes." },
      { q: "¿Puedo pausar mi suscripción?", a: "Las suscripciones no se pueden pausar, solo cancelar. Si necesitas un descanso, puedes cancelar y volver a contratar cuando quieras." },
      { q: "¿Qué pasa cuando contrato? ¿Cuándo empezáis a trabajar?", a: "Desde el primer día. Durante los primeros 14 días realizamos todo el setup de tus servicios (configuraciones, accesos, primeras campañas o calendarios) y a partir de ahí el trabajo es continuo cada mes." },
      { q: "¿Cuántas reuniones incluye mi plan?", a: "Una reunión al mes por cada servicio contratado. Si tienes 2 servicios, tienes 2 reuniones mensuales para revisar cada uno." },
    ],
  },
  {
    id: "servicios",
    label: "Servicios",
    icon: Wrench,
    items: [
      { q: "¿Qué incluye exactamente cada servicio?", a: "Cada servicio tiene su página con dos listas claras: qué incluye y qué no incluye. Trabajamos con total transparencia: lo que está en la lista es exactamente lo que recibes." },
      { q: "¿Quién paga la inversión publicitaria de las campañas?", a: "La inversión en anuncios (Meta, TikTok, Google) la pagas tú directamente a la plataforma desde tu cuenta. Nosotros gestionamos y optimizamos las campañas; el presupuesto publicitario es independiente y siempre tuyo. Recomendamos un mínimo de 300€/mes en Meta o TikTok y de 10€/día en Google para obtener resultados." },
      { q: "¿Cuándo veré resultados de SEO?", a: "El posicionamiento orgánico es un trabajo acumulativo: los primeros resultados suelen verse a partir de los 3-6 meses y crecen con el tiempo. Es la inversión de marketing más rentable a largo plazo, pero no es inmediata. Te lo decimos con honestidad desde el principio." },
      { q: "¿Cómo funcionan los vídeos que producís?", a: "Tienes tres modalidades a tu elección: (1) te pasamos guiones con estructura para que te grabes hablando y nosotros editamos; (2) te damos indicaciones de tomas, grabas tu negocio o productos, y nosotros editamos y añadimos narración; (3) hacemos el vídeo entero con IA, recursos de stock y narración, sin que tengas que grabar nada." },
      { q: "¿Respondéis los comentarios y mensajes de mis redes?", a: "No. Producimos y publicamos tu contenido, pero el community management (responder comentarios y mensajes de tus clientes) queda en tus manos, porque nadie conoce tu negocio y tus clientes como tú." },
      { q: "¿Qué necesito para contratar Email Marketing?", a: "Tener una base de datos propia de contactos o estar captando correos activamente. Nosotros diseñamos y redactamos hasta 4 correos al mes con sus plantillas y dejamos configuradas las automatizaciones básicas. La licencia de Brevo (la herramienta de envío) corre por tu cuenta." },
    ],
  },
  {
    id: "entregas",
    label: "Entregas y revisiones",
    icon: Clipboard,
    items: [
      { q: "¿Puedo revisar el contenido antes de que se publique?", a: "Sí. El contenido de redes sociales, los correos y el resto de piezas pasan por tu aprobación, con 2 tandas de revisión incluidas por pieza. Las excepciones son los creativos de las campañas de publicidad y los artículos de SEO, que publicamos directamente como parte de la optimización continua." },
      { q: "¿Qué es una tanda de revisión?", a: "Un único mensaje con todos los cambios que quieras para una pieza. Así trabajamos rápido y sin idas y venidas infinitas. Cada pieza incluye 2 tandas." },
      { q: "¿Cuánto tiempo tengo para revisar?", a: "72 horas desde que recibes la pieza o el calendario. Si pasado ese plazo no hay respuesta, se da por aprobado y se programa, para que tu contenido salga siempre a tiempo y tus redes no se queden paradas." },
      { q: "¿Cuándo recibo mis informes?", a: "El día de tu ciclo de facturación, cada mes. Recibes el informe de cada servicio contratado con los resultados del período (los servicios de Redes Sociales envían su informe automáticamente vía Metricool)." },
    ],
  },
  {
    id: "puntuales",
    label: "Servicios puntuales",
    icon: Sparkles,
    items: [
      { q: "¿Puedo pagar los servicios puntuales a plazos?", a: "Sí. Todos los servicios puntuales (Branding y CRM) se pueden pagar en 3 plazos con Klarna, sin que tengas que desembolsar todo de golpe." },
      { q: "¿Cuándo empieza a contar el plazo de entrega de mi web?", a: "Desde que recibimos todo tu material (logo, imágenes, textos, accesos). Cuanto antes nos lo facilites, antes la tienes. Los tiempos de revisión y validación por tu parte también afectan al plazo final." },
      { q: "¿Qué ventaja tengo al implementar el CRM con vosotros?", a: "Somos partners oficiales de Kommo. Tu implementación incluye 7 meses de licencia (6 + 1 de regalo, valorados en 273€) con 1 usuario. Y al renovar con nosotros pagas 190€ por semestre en lugar de los 234€ que costaría por tu cuenta." },
      { q: "¿Hacéis facturas?", a: "Por supuesto. Recibes tu factura con cada pago, tanto en suscripciones como en servicios puntuales." },
    ],
  },
  {
    id: "garantias",
    label: "Resultados y garantías",
    icon: ShieldCheck,
    items: [
      { q: "¿Garantizáis resultados?", a: "Te garantizamos trabajo profesional, estrategia y dedicación real. Lo que ninguna agencia honesta puede garantizarte son cifras concretas de ventas o leads, porque dependen de muchos factores (tu producto, tu precio, tu mercado, la competencia, las plataformas). Lo que sí hacemos: medir todo, reportarte con transparencia y optimizar continuamente para mejorar tus resultados mes a mes." },
      { q: "¿Qué pasa si Meta o Google suspenden mi cuenta?", a: "Las plataformas publicitarias tienen sus propias políticas y a veces toman decisiones automatizadas. Si ocurre, te orientamos sobre cómo proceder, aunque la resolución final depende de la plataforma, ya que la cuenta es tuya." },
      { q: "¿Trabajáis con cualquier negocio?", a: "Trabajamos con PYMEs y negocios de prácticamente cualquier sector. No aceptamos proyectos que infrinjan la ley o las políticas de las plataformas publicitarias." },
    ],
  },
  {
    id: "empezar",
    label: "Cómo empezar",
    icon: Rocket,
    items: [
      { q: "¿Cómo contrato?", a: "Elige tu plan o servicio en la web y completa el pago. Inmediatamente después recibirás acceso a nuestro formulario de onboarding para contarnos todo sobre tu negocio, y agendaremos tu primera reunión. En 14 días tu setup estará funcionando." },
      { q: "No tengo claro qué necesito. ¿Qué hago?", a: "Agenda una llamada gratuita con nuestro equipo y te orientamos sin compromiso sobre qué servicios encajan con tu negocio." },
      { q: "¿Qué tengo que preparar para empezar?", a: "Tras contratar te pedimos lo esencial mediante nuestro formulario de onboarding: accesos a tus cuentas y redes, tu logo y materiales de marca, y contexto sobre tu negocio. Te guiamos con vídeos paso a paso para cada acceso, no necesitas conocimientos técnicos." },
    ],
  },
];

export default function FAQPage() {
  const [active, setActive] = useState<string>(categories[0].id);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (i) => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [query]);

  // JSON-LD FAQPage schema
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: categories.flatMap((c) =>
      c.items.map((i) => ({
        "@type": "Question",
        name: i.q,
        acceptedAnswer: { "@type": "Answer", text: i.a },
      }))
    ),
  };

  const visible = query ? filtered : categories.filter((c) => c.id === active);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang="es" />
        <title>Preguntas Frecuentes | Vibra Marketing</title>
        <meta name="description" content="Resuelve tus dudas: permanencia, IVA, revisiones, propiedad de cuentas, plazos y formas de pago de los servicios de Vibra Marketing." />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={`${SITE.url}/faq`} />
        <link rel="alternate" hrefLang="es-ES" href={`${SITE.url}/faq`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Vibra Marketing" />
        <meta property="og:title" content="Preguntas Frecuentes | Vibra Marketing" />
        <meta property="og:description" content="Resuelve tus dudas sobre planes, permanencia, IVA, plazos y pagos en Vibra Marketing." />
        <meta property="og:url" content={`${SITE.url}/faq`} />
        <meta property="og:image" content={SITE.ogImage} />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbForPage([
          { name: "Inicio", path: "/" },
          { name: "Preguntas frecuentes", path: "/faq" },
        ]))}</script>
      </Helmet>
      <Header />

      <main className="relative overflow-hidden pt-28 pb-24">
        {/* Purple aura background */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-[520px] -z-10 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/15 via-primary/5 to-transparent" />
          <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-primary/25 blur-[120px]" />
        </div>

        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-5">
              <HelpCircle size={14} />
              Centro de ayuda
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] mb-5">
              Preguntas <span className="text-gradient">frecuentes</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Todo lo que necesitas saber antes de trabajar con Vibra. Si no encuentras tu respuesta, escríbenos.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-xl mx-auto mb-10">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Busca tu pregunta…"
                className="w-full pl-11 pr-4 py-3.5 rounded-full bg-card border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-[260px_1fr] gap-10 max-w-6xl mx-auto">
            {/* Sidebar / tabs */}
            {!query && (
              <aside className="lg:sticky lg:top-24 lg:self-start">
                <nav className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                  {categories.map((c) => {
                    const Icon = c.icon;
                    const isActive = active === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setActive(c.id)}
                        className={cn(
                          "shrink-0 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left whitespace-nowrap lg:whitespace-normal",
                          isActive
                            ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                            : "text-muted-foreground hover:text-foreground hover:bg-card"
                        )}
                      >
                        <Icon size={16} className="shrink-0" />
                        <span>{c.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </aside>
            )}

            {/* Content */}
            <div className={cn(query && "lg:col-span-2")}>
              {visible.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  No encontramos preguntas que coincidan con "{query}".
                </div>
              )}

              <div className="space-y-10">
                {visible.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <section key={cat.id}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Icon size={18} />
                        </div>
                        <h2 className="text-xl font-bold tracking-tight">{cat.label}</h2>
                      </div>

                      <Accordion
                        type="single"
                        collapsible
                        defaultValue={`${cat.id}-0`}
                        className="rounded-2xl bg-card border border-border/60 overflow-hidden divide-y divide-border/60"
                      >
                        {cat.items.map((item, idx) => (
                          <AccordionItem
                            key={`${cat.id}-${idx}`}
                            value={`${cat.id}-${idx}`}
                            className="border-0 px-5"
                          >
                            <AccordionTrigger className="text-left text-base font-medium py-5 hover:no-underline hover:text-primary transition-colors">
                              {item.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                              {item.a}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </section>
                  );
                })}
              </div>

              {/* CTA */}
              <div className="mt-16 relative overflow-hidden rounded-3xl p-8 md:p-10 bg-gradient-to-br from-primary/15 via-primary/10 to-primary/20 border border-primary/30">
                <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-primary/30 blur-3xl" />
                <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div>
                    <h3 className="text-2xl font-bold tracking-tight mb-2">
                      ¿Tienes otra pregunta?
                    </h3>
                    <p className="text-muted-foreground">
                      Escríbenos por WhatsApp y te respondemos en menos de 24 h.
                    </p>
                  </div>
                  <a
                    href="https://wa.me/34614541275"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-medium hover:shadow-glow hover:-translate-y-0.5 transition-all"
                  >
                    <MessageCircle size={18} />
                    Escribir por WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
